import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { fetchAnalyticsData } from '../services/analyticsService';
import { 
  BarChart, 
  TrendingUp, 
  Users, 
  Map,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  File,
  Menu,
  X
} from 'lucide-react';
import { MapContainer, TileLayer, Circle, Popup } from 'react-leaflet';
import { LineChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AnalyticsData {
  totalComplaints: number;
  resolvedComplaints: number;
  pendingComplaints: number;
  inProgressComplaints: number;
  avgResolutionTime: number;
  categoryData: { category: string; count: number; resolved: number }[];
  monthlyData: { month: string; complaints: number; resolved: number }[];
  locationData: { lat: number; lng: number; count: number; category: string; description?: string }[];
}

const mockAnalyticsData: AnalyticsData = {
  totalComplaints: 1247,
  resolvedComplaints: 892,
  pendingComplaints: 186,
  inProgressComplaints: 169,
  avgResolutionTime: 5.2,
  categoryData: [
    { category: 'roads', count: 324, resolved: 245 },
    { category: 'garbage', count: 287, resolved: 198 },
    { category: 'streetlight', count: 194, resolved: 156 },
    { category: 'water', count: 231, resolved: 167 },
    { category: 'electricity', count: 211, resolved: 126 }
  ],
  monthlyData: [
    { month: 'Jan', complaints: 98, resolved: 76 },
    { month: 'Feb', complaints: 112, resolved: 89 },
    { month: 'Mar', complaints: 127, resolved: 95 },
    { month: 'Apr', complaints: 134, resolved: 108 },
    { month: 'May', complaints: 145, resolved: 122 },
    { month: 'Jun', complaints: 156, resolved: 134 },
    { month: 'Jul', complaints: 148, resolved: 125 },
    { month: 'Aug', complaints: 142, resolved: 118 },
    { month: 'Sep', complaints: 138, resolved: 112 },
    { month: 'Oct', complaints: 147, resolved: 123 },
    { month: 'Nov', complaints: 152, resolved: 128 },
    { month: 'Dec', complaints: 164, resolved: 138 }
  ],
  locationData: [
    // Major cities across India with mock complaints
    { lat: 28.6139, lng: 77.2090, count: 45, category: 'roads', description: 'Potholes and road damage near Connaught Place, New Delhi' },
    { lat: 28.6129, lng: 77.2295, count: 32, category: 'garbage', description: 'Garbage collection delay in Central Delhi' },
    { lat: 28.5355, lng: 77.3910, count: 28, category: 'streetlight', description: 'Street light outages in Noida sector 62' },
    { lat: 28.4595, lng: 77.0266, count: 21, category: 'water', description: 'Water supply issues in Gurgaon' },
    { lat: 28.7041, lng: 77.1025, count: 19, category: 'electricity', description: 'Power cuts in North Delhi' },
    { lat: 28.6691, lng: 77.4538, count: 35, category: 'roads', description: 'Road construction delays in East Delhi' },
    { lat: 28.5245, lng: 77.1855, count: 26, category: 'garbage', description: 'Overflowing garbage bins in South Delhi' },
    { lat: 28.6304, lng: 77.2177, count: 23, category: 'streetlight', description: 'Broken street lights in Old Delhi area' },
    { lat: 19.0760, lng: 72.8777, count: 52, category: 'water', description: 'Water leakage in Mumbai Central' },
    { lat: 19.2183, lng: 72.9781, count: 38, category: 'electricity', description: 'Frequent power outages in Thane' },
    { lat: 19.1706, lng: 72.8301, count: 41, category: 'roads', description: 'Traffic congestion on Linking Road, Bandra' },
    { lat: 19.0330, lng: 73.0297, count: 29, category: 'garbage', description: 'Waste management issues in Navi Mumbai' },
    { lat: 12.9716, lng: 77.5946, count: 47, category: 'roads', description: 'Road repair needed on MG Road, Bangalore' },
    { lat: 12.9141, lng: 77.5955, count: 33, category: 'water', description: 'Irregular water supply in JP Nagar' },
    { lat: 12.9784, lng: 77.6343, count: 26, category: 'electricity', description: 'Power fluctuations in Koramangala' },
    { lat: 13.0018, lng: 77.5726, count: 22, category: 'garbage', description: 'Garbage collection delay in Whitefield' },
    { lat: 13.0827, lng: 77.2707, count: 39, category: 'roads', description: 'Pothole repairs needed in Chennai city center' },
    { lat: 13.0413, lng: 80.2337, count: 31, category: 'water', description: 'Water quality issues in T Nagar' },
    { lat: 13.1171, lng: 80.1952, count: 25, category: 'electricity', description: 'Transformer issues in Guindy' },
    { lat: 13.0072, lng: 80.2574, count: 28, category: 'streetlight', description: 'Street light maintenance in Velachery' },
    { lat: 17.3850, lng: 78.4867, count: 42, category: 'roads', description: 'Road widening project delays in Hyderabad' },
    { lat: 17.4483, lng: 78.3915, count: 35, category: 'garbage', description: 'Waste segregation issues in Hitech City' },
    { lat: 17.3616, lng: 78.4747, count: 27, category: 'water', description: 'Water pressure issues in Gachibowli' },
    { lat: 17.4931, lng: 78.3915, count: 24, category: 'electricity', description: 'Power supply issues in Kondapur' },
    { lat: 22.5726, lng: 88.3639, count: 48, category: 'roads', description: 'Traffic signal malfunction in Kolkata CBD' },
    { lat: 22.5677, lng: 88.4204, count: 36, category: 'water', description: 'Water pipe burst in Salt Lake City' },
    { lat: 22.5411, lng: 88.3372, count: 29, category: 'electricity', description: 'Voltage fluctuations in South Kolkata' },
    { lat: 22.6341, lng: 88.4149, count: 23, category: 'garbage', description: 'Waste disposal issues in Rajarhat' }
  ]
};

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [data, setData] = useState<AnalyticsData>(mockAnalyticsData);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const analyticsData = await fetchAnalyticsData(selectedTimeRange);
        if (analyticsData) {
          setData({
            totalComplaints: analyticsData.totalComplaints,
            resolvedComplaints: analyticsData.resolvedComplaints,
            pendingComplaints: analyticsData.pendingComplaints,
            inProgressComplaints: analyticsData.inProgressComplaints,
            avgResolutionTime: analyticsData.avgResolutionTime,
            categoryData: analyticsData.categoryData.map(item => ({
              category: item.category,
              count: item.count,
              resolved: item.resolved
            })),
            monthlyData: analyticsData.monthlyData,
            locationData: analyticsData.locationData.map(item => ({
              lat: item.lat,
              lng: item.lng,
              count: item.count,
              category: item.category,
              description: (item as any).description || undefined
            }))
          });
        } else {
          setData(mockAnalyticsData);
        }
      } catch (error) {
        console.error('Error loading analytics data:', error);
        setData(mockAnalyticsData);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedTimeRange, t]);

  const exportAnalyticsData = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    csvContent += "Metric,Value\n";
    
    csvContent += `${t('analyticsPage.totalComplaints')},${data.totalComplaints}\n`;
    csvContent += `${t('analyticsPage.resolutionRate')},${((data.resolvedComplaints / (data.totalComplaints || 1)) * 100).toFixed(1)}%\n`;
    csvContent += `${t('analyticsPage.avgResolutionTime')},${data.avgResolutionTime} ${t('analyticsPage.days')}\n`;
    csvContent += `${t('analyticsPage.pendingComplaints')},${data.pendingComplaints}\n`;
    
    csvContent += "\nCategory,Total,Resolved\n";
    
    data.categoryData.forEach(item => {
      csvContent += `${t(`categories.${item.category}`) || item.category},${item.count},${item.resolved}\n`;
    });
    
    csvContent += "\nMonth,Total Complaints,Resolved Complaints\n";
    
    data.monthlyData.forEach(item => {
      csvContent += `${item.month},${item.complaints},${item.resolved}\n`;
    });
    
    csvContent += "\nLocation Latitude,Location Longitude,Complaints,Category\n";
    
    data.locationData.forEach(item => {
      csvContent += `${item.lat},${item.lng},${item.count},${t(`categories.${item.category}`) || item.category}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "analytics_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resolutionRate = ((data.resolvedComplaints / (data.totalComplaints || 1)) * 100).toFixed(1);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      roads: '#EF4444',
      garbage: '#F59E0B',
      streetlight: '#3B82F6',
      water: '#06B6D4',
      electricity: '#8B5CF6'
    };
    return colors[category] || '#6B7280';
  };

  const CategoryChart: React.FC<{ data: typeof mockAnalyticsData.categoryData }> = ({ data }) => {
    const maxCount = Math.max(...data.map(d => d.count));
    
    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={item.category} className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-16 sm:w-20 text-xs text-gray-600 capitalize truncate">
              {t(`categories.${item.category}`) || item.category}
            </div>
            <div className="flex-1 relative">
              <div className="w-full bg-gray-200 rounded-full h-5 sm:h-6 relative overflow-hidden">
                <div 
                  className="bg-blue-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${(item.count / (maxCount || 1)) * 100}%` }}
                />
                <div 
                  className="bg-green-500 h-full rounded-full absolute top-0 left-0 transition-all duration-500"
                  style={{ width: `${(item.resolved / (maxCount || 1)) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Total: {item.count}</span>
                <span>Resolved: {item.resolved}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const MonthlyChart: React.FC<{ data: typeof mockAnalyticsData.monthlyData }> = ({ data }) => {
    const CustomTooltip = ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-2 sm:p-3 rounded-lg shadow-lg border border-gray-200">
            <p className="font-medium text-gray-900 text-sm">{label}</p>
            <p className="text-blue-600 text-xs sm:text-sm">
              {t('analyticsPage.totalComplaints')}: <span className="font-medium">{payload[0]?.value}</span>
            </p>
            <p className="text-green-600 text-xs sm:text-sm">
              {t('analyticsPage.resolvedComplaints')}: <span className="font-medium">{payload[1]?.value}</span>
            </p>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="h-48 sm:h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280" 
              tick={{ fontSize: 10 }}
              tickMargin={5}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#6B7280" 
              tick={{ fontSize: 10 }}
              tickMargin={5}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="top" 
              height={25}
              wrapperStyle={{ paddingBottom: '5px' }}
            />
            <Area 
              type="monotone" 
              dataKey="complaints" 
              stroke="#3B82F6" 
              fill="#3B82F6" 
              fillOpacity={0.1} 
              legendType="none"
            />
            <Area 
              type="monotone" 
              dataKey="resolved" 
              stroke="#10B981" 
              fill="#10B981" 
              fillOpacity={0.1} 
              legendType="none"
            />
            <Line 
              type="monotone" 
              dataKey="complaints" 
              stroke="#3B82F6" 
              strokeWidth={2} 
              dot={{ r: 3, fill: '#3B82F6', strokeWidth: 1, stroke: '#fff' }} 
              activeDot={{ r: 5, fill: '#3B82F6', stroke: '#fff', strokeWidth: 1 }}
              name={t('analyticsPage.totalComplaints')}
              isAnimationActive={false}
            />
            <Line 
              type="monotone" 
              dataKey="resolved" 
              stroke="#10B981" 
              strokeWidth={2} 
              dot={{ r: 3, fill: '#10B981', strokeWidth: 1, stroke: '#fff' }} 
              activeDot={{ r: 5, fill: '#10B981', stroke: '#fff', strokeWidth: 1 }}
              name={t('analyticsPage.resolvedComplaints')}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const GeographicMap: React.FC<{ data: typeof mockAnalyticsData.locationData }> = ({ data }) => {
    const centerLat = data.length > 0 ? data.reduce((sum, point) => sum + point.lat, 0) / data.length : 0;
    const centerLng = data.length > 0 ? data.reduce((sum, point) => sum + point.lng, 0) / data.length : 0;
    
    return (
      <div className="rounded-lg overflow-hidden relative" style={{ height: '300px' }}>
        <MapContainer 
          center={[centerLat || 28.6139, centerLng || 77.2090]} 
          zoom={5} 
          style={{ height: '100%', width: '100%' }}
          className="rounded-lg"
          zoomControl={true}
          scrollWheelZoom={true}
          minZoom={3}
          maxZoom={18}
          worldCopyJump={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {data && data.length > 0 ? (
            data.map((point, index) => {
              const radius = 2000;
              const color = getCategoryColor(point.category);
              const borderColor = color;
              
              if (!point.lat || !point.lng || isNaN(point.lat) || isNaN(point.lng)) {
                return null;
              }
              
              return (
                <Circle
                  key={`${point.lat}-${point.lng}-${index}`}
                  center={[point.lat, point.lng]}
                  radius={radius}
                  fillColor={color}
                  color={borderColor}
                  fillOpacity={0.85}
                  weight={2}
                >
                  <Popup>
                    <div className="min-w-[120px] sm:min-w-[150px]">
                      <div className="font-bold text-sm sm:text-base text-gray-800 mb-1 pb-1 border-b border-gray-200">
                        {t(`categories.${point.category}`) || point.category}
                      </div>
                      <div className="flex items-center mb-1">
                        <span className="font-semibold text-gray-700 mr-1 text-xs">Complaints:</span>
                        <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                          {point.count}
                        </span>
                      </div>
                      {point.description && (
                        <div className="mb-2 p-1.5 bg-gray-50 rounded">
                          <p className="text-gray-700 text-xs">{point.description}</p>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 bg-gray-100 p-1.5 rounded">
                        <span className="font-medium">Location:</span> {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                      </div>
                    </div>
                  </Popup>
                </Circle>
              );
            })
          ) : (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-3 rounded-lg shadow-lg">
              <p className="text-gray-600 text-sm">No location data available</p>
            </div>
          )}
        </MapContainer>
        
        <div className="absolute bottom-2 left-2 bg-white p-2 rounded-lg shadow-lg z-10">
          <div className="text-xs font-medium text-gray-700 mb-1">{t('analyticsPage.categories')}</div>
          <div className="space-y-1">
            {['roads', 'garbage', 'streetlight', 'water', 'electricity'].map(category => (
              <div key={category} className="flex items-center space-x-1.5">
                <div 
                  className="w-2.5 h-2.5 rounded-full" 
                  style={{ backgroundColor: getCategoryColor(category) }}
                />
                <span className="text-xs text-gray-600 capitalize">
                  {t(`categories.${category}`) || category}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center space-x-3">
              <img src="/hero.png" alt="Logo" className="w-8 h-8" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-800 whitespace-nowrap">Analytics</h1>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value as any)}
              className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="7d">{t('analyticsPage.last7Days')}</option>
              <option value="30d">{t('analyticsPage.last30Days')}</option>
              <option value="90d">{t('analyticsPage.last90Days')}</option>
              <option value="1y">{t('analyticsPage.lastYear')}</option>
            </select>
            <button 
              onClick={exportAnalyticsData}
              className="flex items-center space-x-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden xs:inline text-xs">{t('analyticsPage.export')}</span>
            </button>
            <button
              onClick={handleSignOut}
              className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors hidden sm:block"
            >
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="official"
          currentPage="analytics"
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">{t('analyticsPage.totalComplaints')}</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-800">{data.totalComplaints.toLocaleString()}</p>
                </div>
                <File className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">{t('analyticsPage.resolutionRate')}</p>
                  <p className="text-lg sm:text-xl font-bold text-green-600">{resolutionRate}%</p>
                </div>
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">{t('analyticsPage.avgResolutionTime')}</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-600">{data.avgResolutionTime} {t('analyticsPage.days')}</p>
                </div>
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">{t('analyticsPage.pendingComplaints')}</p>
                  <p className="text-lg sm:text-xl font-bold text-yellow-600">{data.pendingComplaints}</p>
                </div>
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">{t('analyticsPage.activeUsers')}</p>
                  <p className="text-lg sm:text-xl font-bold text-purple-600">2,847</p>
                </div>
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Category Analysis */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">{t('analyticsPage.complaintsByCategory')}</h3>
                <BarChart className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </div>
              <CategoryChart data={data.categoryData} />
            </div>

            {/* Monthly Trends */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">{t('analyticsPage.monthlyTrends')}</h3>
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </div>
              <MonthlyChart data={data.monthlyData} />
            </div>
          </div>

          {/* Geographic Map */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">{t('analyticsPage.geographicDistribution')}</h3>
              <Map className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            </div>
            <GeographicMap data={data.locationData} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Analytics;